import { useLazyQuery, useMutation } from "@apollo/client";
import { useAppDispatch } from "hooks/redux";
import { snackbarRequest } from "shared/molecules/snackbar/snackbar-slice";
import { GET_SESSION_ALLOCATIONS } from "utils/graphql/services/live-classes/queries";
import { CREATE_HOST_JOINING_LINK } from "utils/graphql/services/live-classes/mutations";
import liveClassClientService from "utils/graphql/liveclassClient";

export const useHostLink = () => {
    const dispatch = useAppDispatch();

    const [getSessionAllocations] = useLazyQuery(GET_SESSION_ALLOCATIONS, {
        client: liveClassClientService,
    });
    
    const [createJoiningLink] = useMutation(CREATE_HOST_JOINING_LINK, {
        client: liveClassClientService,
    });

    async function handleScheduleCopy(row: any) {
        try {
            // Determine type and userType safely for all edge cases
            const rowType = row?.type ? String(row.type).toUpperCase() : "HOST";
            
            let targetUserType = "Licensed";
            let targetType = "HOST";
            let displayRole = "host";

            if (rowType === "CO_HOST" || rowType === "CO-HOST") {
                targetUserType = "Licensed";
                targetType = "CO_HOST";
                displayRole = "co-host";
            } else if (rowType === "OBSERVER") {
                targetUserType = "Basic";
                targetType = "OBSERVER";
                displayRole = "observer";
            } else {
                targetUserType = "Licensed";
                targetType = "HOST";
                displayRole = "host";
            }

            let vendorUserId = row?.vendorUserId;

            if (!vendorUserId) {
                // Step 1: get session allocations (fallback for Host role where vendorUserId might be missing)
                const allocationResponse = await getSessionAllocations({
                    variables: {
                        input: {
                            liveSessionId: row?.liveSessionId,
                            userType: targetUserType,
                            type: targetType,
                        },
                    },
                });

                const allocations =
                    allocationResponse?.data?.getSessionAllocations?.sessionAllocations || [];

                const validUser = allocations.find(
                    (item: any) => item.userType === targetUserType
                );

                vendorUserId = validUser?.vendorUserId;
            }

            if (!vendorUserId) {
                dispatch(
                    snackbarRequest({
                        message: `No ${targetUserType} ${displayRole} found for this session`,
                        type: "error",
                    })
                );
                return;
            }

            // Step 2: create joining link
            const response = await createJoiningLink({
                variables: {
                    input: {
                        liveclassSessionId: row?.liveSessionId,
                        role: targetType,
                        vendorUserId: vendorUserId,
                    },
                },
            });

            const link =
                response?.data?.createJoiningLink?.hostJoiningLink;

            // Step 3: copy link
            if (link) {
                await navigator.clipboard.writeText(link);

                dispatch(
                    snackbarRequest({
                        message: `${displayRole.charAt(0).toUpperCase() + displayRole.slice(1)} joining link copied`,
                    })
                );
            }

        } catch (error) {
            dispatch(
                snackbarRequest({
                    message: `Failed to copy joining link`,
                    type: "error",
                })
            );
        }
    }

    return { handleScheduleCopy };
};